import { useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useFocusTrap } from "./useFocusTrap";

/**
 * Accessible modal dialog: real buttons, focus trap, Escape and backdrop close (docs/01 section 7).
 * Rendered into `document.body` so a dialog opened from inside a side panel (purchase review,
 * store board editor) is never clipped or covered by the panel's stacking context.
 */
export function Dialog({
  title,
  onClose,
  children,
  footer,
  wide = false,
  closeLabel = "Close",
  describedBy,
  className,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  closeLabel?: string;
  describedBy?: string;
  /** An extra class on the dialog box (e.g. the store screen's wider, phone-full-screen layout). */
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(ref, true, { onEscape: onClose });
  const dialog = (
    <div
      className="dialog-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        className={`dialog${wide ? " wide" : ""}${className ? ` ${className}` : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={describedBy}
        tabIndex={-1}
        data-movement-block="true"
      >
        <div className="panel-header">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="btn btn-ghost icon-btn" aria-label={closeLabel} onClick={onClose}>
            ×
          </button>
        </div>
        <div className="panel-body">{children}</div>
        {footer ? <div className="panel-footer">{footer}</div> : null}
      </div>
    </div>
  );
  // Server rendering (unit tests) has no document: render in place.
  return typeof document === "undefined" ? dialog : createPortal(dialog, document.body);
}
